import { useTranslation } from 'react-i18next';
import {
  Eye,
  Calendar,
  User,
  HardDrive,
  Clock,
  Film,
  Edit2,
  Plus,
} from 'lucide-react';
import { Button } from '@ntconsult/aluma-react';
import {
  formatFileSize,
  formatDuration,
  formatRelativeTime,
  formatDate,
} from '@common/utils/format';
import { useCanEditFile } from '../../hooks/useCanEditFile';
import type { File } from '../../types';

interface VideoInfoTabProps {
  file: File;
  onEdit: () => void;
}

export function VideoInfoTab({ file, onEdit }: VideoInfoTabProps) {
  const { t } = useTranslation('video');
  const canEdit = useCanEditFile(file);

  const uploaderName = file.uploadedByUser?.name || '—';
  const viewCount = file.viewCount ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-foreground-muted">
        <span className="flex items-center gap-1.5">
          <Eye className="w-4 h-4" aria-hidden="true" />
          {viewCount} {viewCount === 1 ? t('views') : t('views_plural')}
        </span>
        <span className="flex items-center gap-1.5">
          <User className="w-4 h-4" aria-hidden="true" />
          {t('postedBy')} <strong className="text-foreground">{uploaderName}</strong>
        </span>
        <span className="flex items-center gap-1.5" title={formatDate(file.createdAt)}>
          <Calendar className="w-4 h-4" aria-hidden="true" />
          {formatRelativeTime(file.createdAt)}
        </span>
      </div>

      <div className="space-y-2 text-sm">
        <DetailRow icon={HardDrive} label={t('info.size')} value={formatFileSize(file.size)} />
        {file.duration ? (
          <DetailRow
            icon={Clock}
            label={t('info.duration')}
            value={formatDuration(file.duration)}
          />
        ) : null}
        <DetailRow
          icon={Film}
          label={t('info.format')}
          value={file.mimeType.split('/')[1]?.toUpperCase() ?? ''}
        />
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium text-foreground-muted">{t('info.description')}</p>
        {file.description ? (
          <p className="text-sm text-foreground whitespace-pre-wrap">{file.description}</p>
        ) : canEdit ? (
          <Button
            variant="ghost"
            size="xs"
            onClick={onEdit}
            leftIcon={<Plus className="w-3 h-3" aria-hidden="true" />}
          >
            {t('info.addDescription')}
          </Button>
        ) : (
          <span className="italic text-sm text-foreground-subtle">{t('info.noDescription')}</span>
        )}
      </div>

      {canEdit && file.description && (
        <Button
          variant="ghost"
          size="xs"
          onClick={onEdit}
          leftIcon={<Edit2 className="w-3 h-3" aria-hidden="true" />}
        >
          {t('edit.button')}
        </Button>
      )}
    </div>
  );
}

interface DetailRowProps {
  icon: React.ElementType;
  label: string;
  value: string;
}

function DetailRow({ icon: Icon, label, value }: DetailRowProps) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border">
      <span className="flex items-center gap-1.5 text-foreground-muted">
        <Icon className="w-3.5 h-3.5" aria-hidden="true" />
        {label}
      </span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}
